import React from "react";

import { useFormikContext } from "formik";
import PropTypes from "prop-types";

import { DealSetupAttachmentsModal } from "manager/components/Modal";
import { ATTACHMENT_TYPES } from "manager/config/constants";
import { SwitchWithCheckboxList } from "shared/components/Form";
import Settings from "shared/components/Settings";
import Skeleton from "shared/components/Skeleton";
import { APPLICANT_TYPES, CONTENT_TYPE } from "shared/config/constants";
import useModal from "shared/hooks/useModal";
import { useVeroFlag } from "shared/utils/featureFlags";
import { VeroFlag } from "shared/utils/veroFlags";

import ApplicationSetupHeader from "../Header";
import { getAttachmentsFilter, mapLegacyAttachmentItems } from "../utils";
import SupportedCountriesSection from "./SupportedCountriesSection";

const applicantInternationalDocuments =
  "requiresApplicantInternationalDocuments";
const guarantorInternationalDocuments =
  "requiresGuarantorInternationalDocuments";
const occupantInternationalDocuments =
  "requiresOccupantInternationalDocuments";

const getRequiredAttachments = getAttachmentsFilter(
  ATTACHMENT_TYPES.INTERNATIONAL_DOCUMENTS
);

const mapIntlItems = (items) =>
  mapLegacyAttachmentItems(items, {
    isInternational: true,
    showOcrTooltip: true,
  });

const RequiredAttachmentsInternationalDocumentsLegacy = ({
  loading,
  disabled,
  forSettingsLabel = "Settings",
}) => {
  const isInternationalDocsEnabled = useVeroFlag(
    VeroFlag.useInternationalDocumentUpload
  );
  const { values, setFieldValue } = useFormikContext();
  const { openModalDialog } = useModal();

  if (!isInternationalDocsEnabled) return null;

  const openAttachmentsModal = ({ items, dealSetupId: objectId, title, newItemProps }) =>
    openModalDialog(DealSetupAttachmentsModal, {
      title,
      items,
      objectId,
      contentType: CONTENT_TYPE.dealSetup,
      forSettingsLabel,
      newItemProps,
    });

  const onChangeAttachment = (id, checked) => {
    const index = values.requiredAttachments?.findIndex(
      (item) => item.id === id
    );
    setFieldValue(`requiredAttachments[${index}].isActive`, checked);
  };

  return (
    <Settings
      header={
        <Skeleton loading={loading}>
          <ApplicationSetupHeader
            title="International Documents Setup"
            subTitle="Manage the document types available for applicants with international income. Hover over the info icon next to a document type to learn more."
          />
          <SupportedCountriesSection />
        </Skeleton>
      }
      nodes={[
        {
          key: 1,
          component: (
            <Skeleton loading={loading}>
              <SwitchWithCheckboxList
                title="Applicant Attachments"
                items={mapIntlItems(
                  getRequiredAttachments(values, APPLICANT_TYPES.applicant)
                )}
                onManageList={() => {
                  openAttachmentsModal({
                    title: "Manage Applicant International Documents",
                    dealSetupId: values.id,
                    items: mapIntlItems(
                      getRequiredAttachments(values, APPLICANT_TYPES.applicant)
                    ),
                    newItemProps: {
                      applicantType: APPLICANT_TYPES.applicant,
                      attachmentType: ATTACHMENT_TYPES.INTERNATIONAL_DOCUMENTS,
                    },
                  });
                }}
                onChange={onChangeAttachment}
                value={values[applicantInternationalDocuments]}
                onChangeTitle={(value) => {
                  setFieldValue(applicantInternationalDocuments, value);
                }}
                disabled={disabled}
              />
            </Skeleton>
          ),
        },
        {
          key: 2,
          component: (
            <Skeleton loading={loading}>
              <SwitchWithCheckboxList
                title="Occupant Attachments"
                description="over 18 years old"
                items={mapIntlItems(
                  getRequiredAttachments(values, APPLICANT_TYPES.occupant)
                )}
                onManageList={() => {
                  openAttachmentsModal({
                    title: "Manage Occupant International Documents",
                    dealSetupId: values.id,
                    items: mapIntlItems(
                      getRequiredAttachments(values, APPLICANT_TYPES.occupant)
                    ),
                    newItemProps: {
                      applicantType: APPLICANT_TYPES.occupant,
                      attachmentType: ATTACHMENT_TYPES.INTERNATIONAL_DOCUMENTS,
                    },
                  });
                }}
                onChange={onChangeAttachment}
                value={values[occupantInternationalDocuments]}
                onChangeTitle={(value) => {
                  setFieldValue(occupantInternationalDocuments, value);
                }}
                disabled={disabled}
              />
            </Skeleton>
          ),
        },
        {
          key: 3,
          component: (
            <Skeleton loading={loading}>
              <SwitchWithCheckboxList
                title="Guarantor Attachments"
                items={mapIntlItems(
                  getRequiredAttachments(values, APPLICANT_TYPES.guarantor)
                )}
                onManageList={() => {
                  openAttachmentsModal({
                    title: "Manage Guarantor International Documents",
                    dealSetupId: values.id,
                    items: mapIntlItems(
                      getRequiredAttachments(values, APPLICANT_TYPES.guarantor)
                    ),
                    newItemProps: {
                      applicantType: APPLICANT_TYPES.guarantor,
                      attachmentType: ATTACHMENT_TYPES.INTERNATIONAL_DOCUMENTS,
                    },
                  });
                }}
                onChange={onChangeAttachment}
                value={values[guarantorInternationalDocuments]}
                onChangeTitle={(value) => {
                  setFieldValue(guarantorInternationalDocuments, value);
                }}
                disabled={disabled}
              />
            </Skeleton>
          ),
        },
      ]}
    />
  );
};

RequiredAttachmentsInternationalDocumentsLegacy.propTypes = {
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  forSettingsLabel: PropTypes.string,
};

RequiredAttachmentsInternationalDocumentsLegacy.defaultProps = {
  loading: false,
  disabled: false,
  forSettingsLabel: "Settings",
};

export default RequiredAttachmentsInternationalDocumentsLegacy;
